<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useDisplay } from 'vuetify';
import ActionButton from '@/components/buttons/ActionButton.vue';
import AfModal from '@/components/overlays/AfModal.vue';
import PageTitle from '@/components/titles/PageTitle.vue';
import Illustration from '@/features/google-review/Illustration.vue';
import BulletList from '@/features/network/invitation-modal/BulletList.vue';
import { useModalStore } from '@/features/overlays/modals/modalStore';
import { setInvitationURLQuery } from '@/api/queries/me/useInvitationUrlQuery';
import Invitation from '@/assets/img/invitation.png';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';

const modalStore = useModalStore();
const { smAndDown } = useDisplay();
const { copy } = useCopyToClipboard();

const enabled = ref(false);
const { data } = useQuery(setInvitationURLQuery(enabled));
const copied = ref(false);
const invitationUrl = computed(() => data.value?.url);

function copyLink() {
  if (!invitationUrl.value) return;
  copy(invitationUrl.value);
  copied.value = true;
  setTimeout(() => (copied.value = false), 3000);
  MixpanelInstance.track('copy invitation link');
}

/**
 * its a temporary solution to trigger the query when the modal is opened
 * we should find a better way to handle this
 * vuetify does not have option to not render the dialog when not used
 */
watch(
  () => modalStore.invitation.visible,
  () => {
    enabled.value = true;
  },
);
</script>

<template>
  <AfModal
    v-model="modalStore.invitation.visible"
    no-divider
    max-width="1000px"
    :header="false"
    :fullscreen="smAndDown"
  >
    <template #content>
      <div class="flex flex-col items-center mt-15">
        <Illustration
          :image="Invitation"
          circle-width="120px"
          img-width="120px"
          class="mb-5"
        ></Illustration>

        <PageTitle class="mb-5">{{ $t('email.candidate.share.title') }}</PageTitle>

        <div class="flex flex-col items-center" :class="{ 'w-50': !smAndDown }">
          <p class="text-body-3 mb-5 text-center">{{ $t('candidate_account.link.body') }}</p>

          <div class="flex justify-center items-center mb-10 flex-col md:flex-row gap-3">
            <div class="link text-body-2 font-light rounded-sm py-2 px-4">
              {{ invitationUrl }}
            </div>
            <v-btn
              :prepend-icon="copied ? 'fat fa-check' : 'fat fa-link'"
              :class="smAndDown ? 'mt-5' : undefined"
              variant="flat"
              color="primary"
              @click="copyLink"
            >
              {{ $t(copied ? 'general.copy.copied' : 'general.copy.action') }}
            </v-btn>
          </div>
        </div>

        <div class="bullet-lists flex justify-center items-center mb-10">
          <BulletList v-if="!smAndDown" emoji="&#129412;" :paragraph="1" />
          <BulletList v-if="!smAndDown" emoji="&#127826;" :paragraph="2" />

          <div v-else class="bullet-lists-mobile flex" :class="{ 'flex-col': smAndDown }">
            <BulletList emoji="&#129412;" :paragraph="1" />
            <BulletList emoji="&#127826;" :paragraph="2" />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <ActionButton cancel @click="() => modalStore.toggle('invitation')">
        {{ $t('general.close') }}
      </ActionButton>
    </template>
  </AfModal>
</template>

<style scoped lang="scss">
.link {
  border: 1px solid rgba(var(--v-theme-border));
  min-width: 300px;
  height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 85vw;
}

.bullet-lists {
  column-gap: 90px;

  &-mobile {
    row-gap: 40px;
  }
}

.list {
  list-style: inside;
}
</style>
